import { StackActions, useNavigation } from '@react-navigation/native';
import { Icon, Text, useTheme } from '@ui-kitten/components';
import dayjs from 'dayjs';
import lodashIsEmpty from 'lodash/isEmpty';
import React, { Fragment, useCallback, useMemo, useState } from 'react';
import { SafeAreaView, TouchableOpacity, View } from 'react-native';
import StyleSheet from 'react-native-media-query';
import { useSelector } from 'react-redux';
import Popup from 'reactjs-popup';

import PickupLogo from '../../Components/SVG/PickupLogo';
import constants from '../../Config/constants';
import { getReservationStoreParams } from '../../Helper';
import useAddress from '../../Hooks/useAddress';
import useAlert, { getConfirmOptions } from '../../Hooks/useAlert';
import useGoBack from '../../Hooks/useGoBack';
import useMealPlan from '../../Hooks/useMealPlan';
import withDatePicker from '../../Hooks/withDatePicker';
import routeList from '../../Routes/list';
import { allCartSelector } from '../../RTK/cart/selectors';
import { checkoutStoreInfoSelector } from '../../RTK/checkout/selectors';
import { whenFilterSelector } from '../../RTK/filter/selectors';
import { shopSelector } from '../../RTK/shop/selectors';
import {
  activeAddressSelector,
  isGuestSelector,
  isLoggedInSelector,
} from '../../RTK/user/selectors';
import {
  selectPage,
  selectReservationCheckoutData,
  selectRoute,
} from '../../RTK/utility/selectors';
import { ReservationCheckout } from '../../Screens/ReservationCheckout/types';
import ThemeColor from '../../Theme/colors';
import ThemeStyle, { globalSpacing } from '../../Theme/styles';
import BreakPoints from '../../Theme/styles/breakpoints';
import {
  ids as platformStyleIds,
  styles as platformStyles,
} from '../../Theme/styles/platform';

import Button from '../Button';
import { LoadingIndicator } from '../Loader';
import SchedulePicker from '../SchedulePicker';
import { AddressSelector } from '../Web/Address';
import { MODALPROMPT } from '../Web/Modal/ModalPrompt/config';
import useModalPrompt from '../Web/Modal/ModalPrompt/hooks/useModalPrompt';
import Search from '../Web/Search';
import SearchReservation from '../Web/SearchReservation';

import PopupProfile from './PopupProfile';

type HeaderProps = {
  disableMenu?: boolean;
  isVisibleSearchStore?: boolean;
  hasBackButton?: boolean;
  backButtonText?: string;
  plain: boolean;
  onPressBack?: () => void;
  store_id?: string;
  onConfirm?: (date: string) => void;
};
const Header = ({
  disableMenu,
  isVisibleSearchStore = true,
  hasBackButton = true,
  backButtonText = 'Back',
  plain = false,
  onPressBack,
  store_id,
  onConfirm,
}: HeaderProps) => {
  const alert = useAlert();
  const navigation = useNavigation<any>();
  const theme = useTheme();
  const { showModalPrompt } = useModalPrompt();
  const route = useSelector(selectRoute);
  const page = useSelector(selectPage);
  const isLoggedIn = useSelector(isLoggedInSelector);
  const isGuest = useSelector(isGuestSelector);
  const { setActiveAddress, isLoading } = useAddress();
  const { isMealPlan } = useMealPlan();

  const [dateVisible, setDateVisible] = useState(false);
  const checkoutInfo = useSelector(checkoutStoreInfoSelector);
  const shopData = useSelector(shopSelector);
  const cart = useSelector(allCartSelector);
  const whenFilter = useSelector(whenFilterSelector);
  const activeAddress = useSelector(activeAddressSelector);

  const itemCount = cart
    .map((item) => item.items)
    .map((arr) => arr.map((obj) => obj.quantity))
    .flat(cart.length)
    .reduce((previousValue, currentValue) => previousValue + currentValue, 0);

  const checkoutData: ReservationCheckout = useSelector(
    selectReservationCheckoutData,
  );
  const isReservationCheckout = Boolean(route.params?.backRoute); // for now if has this it means reservation checkout
  const goBackParams = useMemo(() => {
    // base params for useGoBack hooks
    const params = {
      backRoute: undefined,
      backRouteParams: undefined,
      rootRoute: undefined,
      isDefaultGoBack: !page.isCheckout,
    };
    let resStorePage = routeList.RES_STORE_DETAILS;
    // intended not equalto use strict equality (`===`) here because `exclusiveBooking` can be a string '1' from URL params
    if (sessionStorage.getItem(constants.RES_EXCLUSIVE_SESSION_KEY) == '1') {
      resStorePage = routeList.RES_STORE_DETAILS_BOOK;
    }
    const reservationBackPage = checkoutData?.isReservation
      ? resStorePage
      : routeList.RES_EVENT_DETAILS;
    if (page.isReservation) {
      // prepare reservation params
      params.backRoute = reservationBackPage;
      if (checkoutData?.isReservation) {
        // back route params if checkout is reservation, back to reservation store details page
        params.backRouteParams = getReservationStoreParams(
          { checkoutData },
          constants.RESERVATION_TYPE.DINING,
        );
      } else {
        // back route params if checkout is event, back to event details page
        params.backRouteParams = {
          eventId: checkoutData?.itemId,
          size: checkoutData?.size,
        };
      }
    } else if (page.isCheckoutWebview) {
      // checkout webview and has backRoute on checkoutData
      params.rootRoute = isReservationCheckout
        ? routeList.RESERVATION_TAB
        : routeList.CHECKOUT;
      if (isReservationCheckout) {
        params.backRouteParams = {
          screen: routeList.RES_CHECKOUT,
        };
      }
    } else {
      // prepare order params
      params.rootRoute = routeList.STORE;
      params.backRouteParams = {
        id: page.isCheckout ? checkoutInfo?.store_id : store_id,
      };
    }
    return params;
  }, [
    checkoutData,
    checkoutInfo?.store_id,
    isReservationCheckout,
    page,
    store_id,
  ]);
  const goBack = useGoBack(goBackParams);

  const toggleLeftDrawer = () =>
    navigation.getParent('LeftDrawer').toggleDrawer();
  const toggleRightrawer = () =>
    navigation.getParent('RightDrawer').toggleDrawer();

  const onPressActiveAddress = (address) =>
    setActiveAddress({ ...address, storeId: shopData?.id });

  const DatePickerContent = () =>
    !page.isCheckout && (
      <Button
        testID="dropDownButton"
        disabled={isMealPlan}
        onPress={() => setDateVisible(true)}
        style={ThemeStyle.flexDirectionRowCenterCenter}
        plain
      >
        <Text category="p1" numberOfLines={1}>
          {lodashIsEmpty(whenFilter)
            ? 'ASAP'
            : dayjs(whenFilter?.value).format(
                constants.DATE_TIME_FILTER_DISPLAY,
              )}
        </Text>
        <Icon name="chevron-down" fill={ThemeColor.green} style={styles.icon} />
      </Button>
    );

  const AddressContent = () => {
    return (
      <Popup
        trigger={
          <button style={styles.popupButton}>
            <View
              style={[
                ThemeStyle.flexDirectionRowCenterCenter,
                { minWidth: 100, maxWidth: 200 },
              ]}
            >
              <Text category="p1" numberOfLines={1}>
                {!lodashIsEmpty(activeAddress)
                  ? activeAddress.formatted_address
                  : 'Address'}
              </Text>
              <Icon
                name="chevron-down"
                fill={ThemeColor.green}
                style={[styles.rightIcon, styles.icon]}
              />
            </View>
          </button>
        }
        arrow={false}
        contentStyle={styles.popupAddressContainer}
        position="bottom center"
        nested
      >
        <View
          dataSet={{ media: ids.saving_address_container }}
          style={styles.saving_address_container}
        >
          {
            //@ts-ignore
            <AddressSelector
              addNewAddress={onAddNewAddress}
              onSelectedSavedAddress={onPressActiveAddress}
              onSelectedSavedAddressAsEdit={onSelectedSavedAddressAsEdit}
            />
          }
        </View>
      </Popup>
    );
  };

  const authenticationContent = () => (
    <View style={styles.authenticationContainer}>
      <Button
        testID="loginButton"
        onPress={onLogin}
        style={[
          styles.loginButton,
          ThemeStyle.flexDirectionRowCenter,
          ThemeStyle.pageHorizontalSpacingSmall,
        ]}
        plain
      >
        <Text status="basic" category="p2">
          Log in
        </Text>
        <Text
          status="basic"
          category="p2"
          dataSet={{ media: platformStyleIds.mobileAndTabletOnly }}
          style={platformStyles.mobileAndTabletOnly}
        >
          {' / '}Sign Up
        </Text>
      </Button>

      <Button
        testID="signupButton"
        onPress={onSignup}
        dataSet={{ media: platformStyleIds.webOnly }}
        style={[styles.signupButton, platformStyles.webOnly]}
        plain
      >
        <Text status="control" category="p2">
          Sign Up
        </Text>
      </Button>
    </View>
  );

  const onSelectedSavedAddressAsEdit = (index, item) => {
    showModalPrompt(MODALPROMPT.addressDetails, {
      status: 'edit',
      selectedAddress: {
        ...item,
        index: item.id,
        latitude: item?.geo_json_point.coordinates[1],
        longitude: item?.geo_json_point.coordinates[0],
      },
    });
  };

  const onLogin = () => {
    showModalPrompt(MODALPROMPT.authentication, {
      isLogin: true,
      navigation: navigation,
      ...(page.isReservation && {
        color: theme['color-info-500'],
        uiKittenStatus: 'info',
      }),
    });
  };

  const onSignup = () => {
    showModalPrompt(MODALPROMPT.authentication, {
      isLogin: false,
      navigation: navigation,
      ...(page.isReservation && {
        color: theme['color-info-500'],
        uiKittenStatus: 'info',
      }),
    });
  };

  const onAddNewAddress = () =>
    showModalPrompt(MODALPROMPT.addressDetails, {
      status: 'new',
      selectedAddress: {},
    });

  const _goBack = useCallback(() => {
    if (typeof onPressBack === 'function') {
      onPressBack();
    } else if (isMealPlan) {
      alert(
        'Are you sure you want to leave?',
        'Your current meal plan will be lost.',
        getConfirmOptions({ onConfirm: goBack }),
      );
    } else {
      goBack();
    }
  }, [isMealPlan, alert, goBack, onPressBack]);

  if (plain || page.isCheckout) {
    return (
      <View style={styles.wrapper}>
        {hasBackButton && (
          <Button
            style={[ThemeStyle.flex1, ThemeStyle.flexDirectionRowCenter]}
            onPress={_goBack}
            plain
          >
            <Icon
              name="chevron-left"
              fill={
                page.isReservation || isReservationCheckout
                  ? theme['color-info-500']
                  : ThemeColor.green
              }
              style={styles.chevronIcon}
            />
            <Text>{backButtonText}</Text>
          </Button>
        )}
        <View
          style={[
            ThemeStyle.flex1,
            ThemeStyle.pageHorizontalSpacingSmall,
            ThemeStyle.alignItemsCenter,
          ]}
        >
          <PickupLogo
            fill1={
              (page.isReservation || isReservationCheckout) &&
              theme['color-info-500']
            }
            width={130}
            height={50}
          />
        </View>
        <View
          dataSet={{ media: platformStyleIds.tabletAndWebOnly }}
          style={[ThemeStyle.flex1, platformStyles.tabletAndWebOnly]}
        />
      </View>
    );
  }

  if (
    page.name === routeList.MY_RESERVATION_DETAILS &&
    ((!isLoggedIn && !isGuest) ||
      sessionStorage.getItem(constants.RES_EXCLUSIVE_SESSION_KEY) == '1') // intended not equalto use strict equality (`===`) here because `exclusiveBooking` can be a string '1' from URL params
  ) {
    // no header on reservation details if not login and not guest
    return null;
  }

  return (
    <Fragment>
      <SafeAreaView style={styles.wrapper}>
        <View
          dataSet={{ media: ids.leftColumnHeader }}
          style={styles.leftColumnHeader}
        >
          {!page.isStore ? (
            <Button
              testID="menu_btn"
              onPress={toggleLeftDrawer}
              plain
              disabled={disableMenu}
            >
              <Icon
                name="menu"
                fill={
                  page.isReservation
                    ? theme['color-info-500']
                    : theme['color-success-500']
                }
                style={styles.icon}
              />
            </Button>
          ) : (
            <Button
              onPress={_goBack}
              style={styles.plainBackButtonContainer}
              plain
            >
              <Icon
                name="chevron-left"
                fill={theme['color-success-500']}
                style={styles.chevronIcon}
              />
            </Button>
          )}

          <View
            dataSet={{ media: ids.logoContainer }}
            style={[styles.logoContainer, ThemeStyle.spacingLeft]}
          >
            <TouchableOpacity
              onPress={() => {
                navigation.dispatch(
                  StackActions.replace(routeList.HOME, {
                    screen: 'MainMenu',
                    params: {
                      screen: page.isReservation
                        ? routeList.RESERVATION_TAB
                        : routeList.HOME_TAB,
                    },
                  }),
                );
              }}
            >
              <PickupLogo
                fill1={page.isReservation && theme['color-info-500']}
                width={130}
                height={50}
              />
            </TouchableOpacity>
          </View>

          {!page.isReservation && (
            <View
              dataSet={{ media: ids.dateContainer }}
              style={styles.dateContainer}
            >
              <DatePickerContent />
              <Fragment>
                {!isMealPlan && <Text category="p1">to</Text>}
                <AddressContent />
              </Fragment>
            </View>
          )}
        </View>

        <View
          dataSet={{ media: ids.rightColumnHeader }}
          style={styles.rightColumnHeader}
        >
          <View
            dataSet={{
              media: ids.searchContainer,
            }}
            style={styles.searchContainer}
          >
            {isVisibleSearchStore &&
              !page.isStore &&
              (page.isReservation ? <SearchReservation /> : <Search />)}
          </View>
          {!isLoggedIn && authenticationContent()}
          <View
            dataSet={{ media: ids.cartContainer }}
            style={styles.cartContainer}
          >
            {(isLoggedIn || isGuest) && !page.isReservation && (
              <Button
                testID="cartButton"
                onPress={toggleRightrawer}
                style={styles.cartButton}
                plain
              >
                <Icon
                  name="shopping-cart-outline"
                  fill={ThemeColor.white}
                  style={styles.icon}
                />

                {itemCount > 0 && (
                  <View style={styles.cartTxtContainer}>
                    <Text style={styles.itemCountTxt}>{itemCount}</Text>
                  </View>
                )}
              </Button>
            )}
          </View>
        </View>
        <PopupProfile />
      </SafeAreaView>
      <LoadingIndicator isOpen={isLoading} />
      {
        //To render again when opening the modal
        //So it will prevent to retain the current state
        //It will depends on the current whenfilter selector
        dateVisible && (
          <SchedulePicker
            storeId={shopData?.id}
            show={dateVisible}
            onCancel={() => setDateVisible(false)}
            onConfirm={(date) => {
              onConfirm(date);
              setDateVisible(false);
            }}
          />
        )
      }
    </Fragment>
  );
};
const { ids, styles } = StyleSheet.create({
  wrapper: {
    ...ThemeStyle.flexDirectionRowCenterCenter,
    borderBottomColor: ThemeColor.gray,
    borderBottomWidth: 1,
    height: 65,
    paddingHorizontal: globalSpacing * 1.5,
  },
  leftColumnHeader: {
    ...ThemeStyle.flexDirectionRowCenter,
    height: 65,
  },
  rightColumnHeader: {
    ...ThemeStyle.flex1,
    height: 65,
    columnGap: 10,
    flexDirection: 'row',
    alignItems: 'center',
    [`@media ${BreakPoints.xs}`]: {
      justifyContent: 'flex-end',
    },
    [`@media ${BreakPoints.sm}`]: {
      justifyContent: 'flex-end',
    },
    [`@media ${BreakPoints.md}`]: {
      justifyContent: 'flex-end',
    },
    [`@media ${BreakPoints.lg}`]: {
      justifyContent: 'flex-start',
    },
    [`@media ${BreakPoints.xl}`]: {
      justifyContent: 'flex-start',
    },
    [`@media ${BreakPoints.xxl}`]: {
      justifyContent: 'flex-start',
    },
  },
  searchContainer: {
    ...ThemeStyle.flex1,
    ...ThemeStyle.spacingLeft,
    [`@media ${BreakPoints.xs}`]: {
      display: 'none',
    },
    [`@media ${BreakPoints.sm}`]: {
      display: 'none',
    },
    [`@media ${BreakPoints.md}`]: {
      display: 'flex',
    },
    [`@media ${BreakPoints.lg}`]: {
      display: 'flex',
    },
    [`@media ${BreakPoints.xl}`]: {
      display: 'flex',
    },
    [`@media ${BreakPoints.xxl}`]: {
      display: 'flex',
    },
  },
  logoContainer: {
    [`@media ${BreakPoints.xs}`]: {
      display: 'none',
    },
    [`@media ${BreakPoints.sm}`]: {
      display: 'flex',
    },
    [`@media ${BreakPoints.md}`]: {
      display: 'flex',
    },
    [`@media ${BreakPoints.lg}`]: {
      display: 'flex',
    },
    [`@media ${BreakPoints.xl}`]: {
      display: 'flex',
    },
    [`@media ${BreakPoints.xxl}`]: {
      display: 'flex',
    },
  },
  chevronIcon: { width: 30, height: 30 },
  icon: { width: 20, height: 20 },
  authenticationContainer: {
    flexDirection: 'row',
    columnGap: 10,
  },
  loginButton: {
    minWidth: 100,
    height: 40,
    backgroundColor: '#E6E6E6',
    borderWidth: 1,
    borderColor: '#2B2B2B',
    borderRadius: 30,
    justifyContent: 'center',
    alignItems: 'center',
  },
  signupButton: {
    width: 100,
    height: 40,
    backgroundColor: ThemeColor.black,
    borderWidth: 1,
    borderColor: '#2B2B2B',
    borderRadius: 30,
    justifyContent: 'center',
    alignItems: 'center',
  },
  cartContainer: {
    zIndex: -1,
    [`@media ${BreakPoints.xs}`]: {
      display: 'flex',
    },
    [`@media ${BreakPoints.sm}`]: {
      display: 'flex',
    },
    [`@media ${BreakPoints.md}`]: {
      display: 'flex',
    },
    [`@media ${BreakPoints.lg}`]: {
      display: 'flex',
    },
    [`@media ${BreakPoints.xl}`]: {
      display: 'flex',
    },
    [`@media ${BreakPoints.xxl}`]: {
      display: 'none',
    },
  },
  cartButton: {
    width: 75,
    height: 40,
    borderRadius: 30,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: ThemeColor.green,
  },
  cartTxtContainer: { width: 25, alignItems: 'center' },
  dateContainer: {
    ...ThemeStyle.spacingLeft,
    ...ThemeStyle.flexDirectionRowCenter,
    height: 30,
    columnGap: 10,
    [`@media ${BreakPoints.xs}`]: {
      display: 'none',
    },
    [`@media ${BreakPoints.sm}`]: {
      display: 'none',
    },
    [`@media ${BreakPoints.md}`]: {
      display: 'none',
    },
    [`@media ${BreakPoints.lg}`]: {
      display: 'none',
    },
    [`@media ${BreakPoints.xl}`]: {
      display: 'flex',
    },
    [`@media ${BreakPoints.xxl}`]: {
      display: 'flex',
    },
  },
  popupButton: {
    backgroundColor: ThemeColor.white,
    borderWidth: 0,
    cursor: 'pointer',
  },
  popupAddressContainer: {
    width: 400,
    borderWidth: 0,
    backgroundColor: 'transparent',
  },
  saving_address_container: {
    backgroundColor: ThemeColor.white,
    top: 30,
    paddingTop: '2%',
    paddingBottom: '2%',
    borderWidth: 1,
    borderColor: ThemeColor.lightGray,
    borderRadius: 5,
    [`@media ${BreakPoints.xs}`]: {
      width: 250,
    },
    [`@media ${BreakPoints.sm}`]: {
      width: 300,
    },
    [`@media ${BreakPoints.md}`]: {
      width: 350,
    },
    [`@media ${BreakPoints.lg}`]: {
      width: 400,
    },
    [`@media ${BreakPoints.xl}`]: {
      width: 450,
    },
    [`@media ${BreakPoints.xxl}`]: {
      width: 500,
    },
  },
  plainBackButtonContainer: {
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center',
  },
  itemCountTxt: { color: ThemeColor.white, fontSize: 18 },
  rightIcon: {
    marginLeft: globalSpacing / 4,
  },
});
export default withDatePicker(React.memo(Header));
